import { Alert, AlertIcon, Button, Stack, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { MfaMethod } from 'types';

import TotpCodeInput from '../../../atoms/totp-code-input';
import MfaTotpIcon from '../../../atoms/mfa-totp-icon';
import MfaSmsIcon from '../../../atoms/mfa-sms-icon';
import { MFAHeader } from '../MFAHeader';
import { Title } from '../../../atoms/Title';

export interface IMfaAuth {
  isMobile?: boolean;
  method?: string;
  uniLogo: string;
  serverErrors?: string[];
  onSubmit: (code: string) => void;
  onResendCode: () => void;
  onAuthHelp: () => void;
  onSignOut?: () => void;
}

const getHeadingText = (method: string | undefined): string => {
  switch (method) {
    case MfaMethod.TOTP:
      return 'Use an authenticator app';
    case MfaMethod.SMS:
      return 'Send a code to a phone number';
    default:
      return '';
  }
};

const getContentHeadingText = (method: string | undefined): string => {
  switch (method) {
    case MfaMethod.TOTP:
      return 'Enter the 6-digit code found in your authenticator app';
    case MfaMethod.SMS:
      return 'Enter the 6-digit code that has been sent as a text message';
    default:
      return '';
  }
};

const getIcon = (method: string | undefined): React.ReactNode => {
  switch (method) {
    case MfaMethod.TOTP:
      return <MfaTotpIcon />;
    case MfaMethod.SMS:
      return <MfaSmsIcon />;
    default:
      return null;
  }
};

export function MfaAuthOnly({ method, serverErrors, onResendCode, onSubmit, onAuthHelp }: IMfaAuth) {
  const [code, setCode] = useState('');
  const headingText = getHeadingText(method);
  const contentHeading = getContentHeadingText(method);
  const icon = getIcon(method);

  const onCodeChange = (value: string) => {
    setCode(value);
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    onSubmit(code);
  };

  return (
    <>
      <Title title={headingText} />

      <MFAHeader icon={icon}>{headingText}</MFAHeader>

      <Text fontWeight={'medium'} fontSize={'lg'}>
        {contentHeading}
      </Text>

      <form onSubmit={handleSubmit}>
        <TotpCodeInput onChange={onCodeChange} digits={6} value={code} />

        {serverErrors?.length > 0 && (
          <Alert status="error" mt={6} mb={6}>
            <AlertIcon />
            The code is invalid or expired.
          </Alert>
        )}

        <Stack flexDirection={'column'} spacing={3} mt={8}>
          <Button type="submit" size={'lg'} variant={'solid'} data-selector="mfa-auth-confirm">
            Enter Code
          </Button>

          {method !== MfaMethod.TOTP && (
            <Button onClick={onResendCode} size={'lg'}>
              Resend code
            </Button>
          )}

          <Button onClick={onAuthHelp} size={'lg'} variant={'ghost'}>
            Need help authenticating?
          </Button>
        </Stack>
      </form>
    </>
  );
}
